import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../u2000Core/axios/U2000UseAxiosAuthenticated";
import { U2202ContextProvider } from "./U2202Context";
import { Outlet } from "react-router-dom";
import i18next from "i18next";
import { doInitLU, defaultU2202InitLU, luHandleAppiError } from "./U2202Service";
import { useU2000InitLUHandlers } from "../u2000Core/hooks/handler/U2000UseInitLUHandlers";
import { U2000HandleApiError } from "../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../u2000Core/context/toast/U2000ToastContext";
import { useU2000LogicalUnitContext } from "../u2000Core/context/logicalUnit/U2000LogicalUnitContext";
import type { U2202InitLU } from "./U2202Service";

export const NS = "u2202";

export const U2202ROLES = {
  READ: "U2202:READ",
} as const;
export type U2202ROLES = (typeof U2202ROLES)[keyof typeof U2202ROLES];

const U2202: React.FC = () => {
  const { i18n } = useTranslation();
  const [i18nReady, setI18nReady] = useState<boolean>(false);
  const { t } = useTranslation();
  const toast = useToast();
  const [initLUReady, setInitLUReady] = useState<boolean>(false);
  const { setLogicalUnitTitle, setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const [initLUData, setInitLUData] = useState<U2202InitLU>(defaultU2202InitLU);

  useEffect(() => {
    i18next.loadNamespaces(["u2202"], (err) => {
      if (err) return console.error("Error loading namespace:", err);
      setI18nReady(true);
    });
  }, [i18n]);

  const handleInitLU = useU2000InitLUHandlers<U2202InitLU>({
    doInitLUFn: async () => {
      return await doInitLU(u2000AxiosAuthenticated);
    },
    onSuccess: (initLU: U2202InitLU) => {
      setInitLUData(initLU);
      setInitLUReady(true);
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
      setInitLUReady(true);
    },
  });

  useEffect(() => {
    handleInitLU.getInitLU();
  }, [t]);

  useEffect(() => {
    if (!i18nReady || !initLUReady) return;

    const title = t("title", { ns: NS });
    setLogicalUnitTitle(title);

    return () => {
      setLogicalUnitTitle(null);
      setLogicalUnitBreadcrumb([]);
    };
  }, [i18nReady, initLUReady, t, setLogicalUnitTitle, setLogicalUnitBreadcrumb]);

  if (!i18nReady || !initLUReady) return null;

  return (
    <U2202ContextProvider initLU={initLUData}>
      <Outlet />
    </U2202ContextProvider>
  );
};

export default U2202;
